import { ApiResponse, PaginatedResponse } from "../types/common";
import {
  Course,
  CreateCourseRequest,
  UpdateCourseRequest,
  CourseFilter,
  CourseStatistics,
} from "../types/course";
import api from "./api";

// Course CRUD Operations
export const getCourses = async (
  filters: CourseFilter = {}
): Promise<PaginatedResponse<Course>> => {
  const params = new URLSearchParams();

  if (filters.page) params.append("page", filters.page.toString());
  if (filters.limit) params.append("limit", filters.limit.toString());
  if (filters.search) params.append("search", filters.search);
  if (filters.level) params.append("level", filters.level);
  if (filters.category) params.append("category", filters.category);
  if (filters.departmentId) params.append("departmentId", filters.departmentId);
  if (filters.isActive !== undefined)
    params.append("isActive", filters.isActive.toString());

  const response = await api.get(`/courses?${params.toString()}`);
  // API interceptor returns response.data, so response is the actual backend data
  const data = response as any;

  // Ensure we return the correct structure for PaginatedResponse
  return {
    success: data.success || true,
    data: data.courses || [],
    pagination: {
      currentPage: data.currentPage || 1,
      totalPages: data.totalPages || 1,
      totalItems: data.total || 0,
      hasNext: (data.currentPage || 1) < (data.totalPages || 1),
      hasPrev: (data.currentPage || 1) > 1,
    },
  };
};

export const getCourseById = async (
  id: string
): Promise<ApiResponse<Course>> => {
  const response = await api.get(`/courses/${id}`);
  return response.data || response;
};

export const createCourse = async (
  data: CreateCourseRequest
): Promise<ApiResponse<Course>> => {
  try {
    const response = await api.post("/courses", data);
    return response.data || response;
  } catch (error: any) {
    console.error("Error creating course:", error);
    throw error; // Re-throw to handle in component
  }
};

export const updateCourse = async (
  id: string,
  data: UpdateCourseRequest
): Promise<ApiResponse<Course>> => {
  try {
    const response = await api.put(`/courses/${id}`, data);
    return response.data || response;
  } catch (error: any) {
    console.error("Error updating course:", error);
    throw error; // Re-throw to handle in component
  }
};

export const deleteCourse = async (id: string): Promise<ApiResponse<void>> => {
  const response = await api.delete(`/courses/${id}`);
  return response.data || response;
};

// Course Statistics (Note: This endpoint may not exist, handle gracefully)
export const getCourseStats = async (
  branchId?: string
): Promise<ApiResponse<CourseStatistics>> => {
  try {
    const params = branchId ? `?branchId=${branchId}` : "";
    const response = await api.get(`/courses/statistics${params}`);

    // Transform API response structure from { success, statistics } to { success, data }
    if (response && (response as any).statistics) {
      const transformedData = {
        success: (response as any).success,
        data: (response as any).statistics,
      };
      return transformedData;
    }

    return response.data || response;
  } catch (error) {
    // Return mock data if stats endpoint doesn't exist
    console.warn("Course stats endpoint not available, returning mock data");
    return {
      success: true,
      data: {
        totalCourses: 0,
        activeCourses: 0,
        inactiveCourses: 0,
        coursesByCategory: {
          core: 0,
          elective: 0,
          practical: 0,
          theory: 0,
        },
        coursesByLevel: {
          Beginner: 0,
          Intermediate: 0,
          Advanced: 0,
        },
        averageCredits: 0,
      },
      message: "Course statistics (mock data)",
    };
  }
};

// Bulk Operations
export const bulkUpdateCourses = async (
  ids: string[],
  data: Partial<UpdateCourseRequest>
): Promise<ApiResponse<void>> => {
  const response = await api.put("/courses/bulk", { ids, data });
  return response.data || response;
};

export const bulkDeleteCourses = async (
  ids: string[]
): Promise<ApiResponse<void>> => {
  const response = await api.delete("/courses/bulk", { data: { ids } });
  return response.data || response;
};
